import { splitProps, type Component, type JSX } from 'solid-js';
import { Dynamic } from 'solid-js/web';

type TextSize = 'sm' | 'xs';
type TextWeight = 'normal' | 'medium';
type TextTone = 'default' | 'foreground' | 'muted' | 'green' | 'red' | 'blue';
type TextElement = 'span' | 'div' | 'p' | 'label';

const sizeClasses: Record<TextSize, string> = {
    sm: 'text-sm',
    xs: 'text-xs',
};

const weightClasses: Record<TextWeight, string> = {
    normal: 'font-normal',
    medium: 'font-medium',
};

const toneClasses: Record<TextTone, string> = {
    default: '',
    foreground: 'text-foreground',
    muted: 'text-muted-foreground',
    green: 'text-green',
    red: 'text-red',
    blue: 'text-blue',
};

function mergeClasses(...classes: (string | undefined)[]): string {
    return classes.filter(Boolean).join(' ');
}

export interface TextProps {
    as?: TextElement;
    size?: TextSize;
    weight?: TextWeight;
    tone?: TextTone;
    class?: string;
    children?: JSX.Element;
    [key: string]: any;
}

const Text: Component<TextProps> = (props) => {
    const [local, others] = splitProps(props, ['as', 'size', 'weight', 'tone', 'class', 'children']);

    const classes = () => mergeClasses(local.size ? sizeClasses[local.size] : undefined, local.weight ? weightClasses[local.weight] : undefined, local.tone ? toneClasses[local.tone] : undefined, local.class);

    return (
        <Dynamic component={local.as ?? 'span'} class={classes()} {...others}>
            {local.children}
        </Dynamic>
    );
};

type HeadingLevel = 1 | 2;

export interface HeadingProps {
    level?: HeadingLevel;
    class?: string;
    children?: JSX.Element;
    [key: string]: any;
}

const Heading: Component<HeadingProps> = (props) => {
    const [local, others] = splitProps(props, ['level', 'class', 'children']);
    const tag = () => `h${local.level ?? 1}` as 'h1' | 'h2';

    return (
        <Dynamic component={tag()} class={local.class} {...others}>
            {local.children}
        </Dynamic>
    );
};

export { Text, Heading };
